import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import type { LineKind, ProfitShareLine } from '../../services/profit-share.service';
import { KIND_HELP, KIND_LABEL, monthLabel, thisMonth } from './profit-share.format';

interface Base { open: boolean; onClose: () => void; busy?: boolean; error?: string | null }

export function TermsDialog({ open, onClose, onSave, busy, error, firstOpenMonth, currentPct }: Base & {
  onSave: (v: { effective_month: string; share_pct: number; note: string }) => void; firstOpenMonth?: string; currentPct?: number;
}) {
  const [month, setMonth] = useState(thisMonth());
  const [pct, setPct] = useState('30');
  const [note, setNote] = useState('');
  useEffect(() => { if (open) { setMonth(firstOpenMonth ?? thisMonth()); setPct(String(currentPct ?? 30)); setNote(''); } }, [open, firstOpenMonth, currentPct]);
  const n = Number(pct);
  const valid = /^\d{4}-\d{2}$/.test(month) && pct !== '' && n >= 0 && n <= 100;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set the partner’s share</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField type="month" label="From month (UTC)" value={month} onChange={(e) => setMonth(e.target.value)}
            helperText={firstOpenMonth ? `Applies from this month on. Closed months keep their share; the earliest open month is ${monthLabel(firstOpenMonth)}.` : 'Applies from this month on.'}
            slotProps={{ inputLabel: { shrink: true } }} />
          <TextField type="number" label="Partner share %" value={pct} onChange={(e) => setPct(e.target.value)}
            slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }} />
          <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || busy} onClick={() => onSave({ effective_month: month, share_pct: n, note })}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function LineDialog({ open, onClose, onSave, busy, error, line, defaultMonth }: Base & {
  onSave: (v: { month: string; kind: LineKind; amount: number; note: string }) => void; line?: ProfitShareLine | null; defaultMonth: string;
}) {
  const [month, setMonth] = useState(defaultMonth);
  const [kind, setKind] = useState<LineKind>('revenue_adjustment');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!open) return;
    setMonth(line?.month ?? defaultMonth);
    setKind(line?.kind ?? 'revenue_adjustment');
    setAmount(line ? String(line.amount) : '');
    setNote(line?.note ?? '');
  }, [open, line, defaultMonth]);
  const n = Number(amount);
  const signOk = kind === 'revenue_adjustment' ? n !== 0 : n > 0;
  const valid = /^\d{4}-\d{2}$/.test(month) && amount !== '' && Number.isFinite(n) && signOk && note.trim() !== '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{line ? 'Edit line' : 'Add a line'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField type="month" label="Month (UTC)" value={month} onChange={(e) => setMonth(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as LineKind)} helperText={KIND_HELP[kind]}>
            {(Object.keys(KIND_LABEL) as LineKind[]).map((k) => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
          </TextField>
          <TextField type="number" label="Amount ($)" value={amount} onChange={(e) => setAmount(e.target.value)}
            error={amount !== '' && !signOk}
            helperText={amount !== '' && !signOk ? (kind === 'revenue_adjustment' ? 'Not zero' : 'A positive amount') : ' '} />
          <TextField label="Note" required value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why — the partner sees this" multiline minRows={2} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || busy} onClick={() => onSave({ month, kind, amount: n, note: note.trim() })}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function PaidDialog({ open, onClose, onSave, busy, error, month, payout }: Base & {
  onSave: (v: { amount: number; paid_on: string; note: string }) => void; month: string; payout: number;
}) {
  const [amount, setAmount] = useState(String(payout));
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  useEffect(() => { if (open) { setAmount(String(payout)); setOn(new Date().toISOString().slice(0, 10)); setNote(''); } }, [open, payout]);
  const n = Number(amount);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark {month && monthLabel(month)} paid</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField type="number" label="Amount paid ($)" value={amount} onChange={(e) => setAmount(e.target.value)} helperText={`The statement’s payout is $${payout}`} />
          <TextField type="date" label="Paid on" value={on} onChange={(e) => setOn(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. bank transfer ref" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy || amount === '' || !(n >= 0) || !on} onClick={() => onSave({ amount: n, paid_on: on, note })}>Mark paid</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, busy, error, title, body, confirm }: Base & {
  onConfirm: () => void; title: string; body: string; confirm: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{body}</Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy} onClick={onConfirm}>{confirm}</Button>
      </DialogActions>
    </Dialog>
  );
}
